import _ from 'lodash';

import {
  LoanOptionPriceStatus,
  LoanProgramsFilterIds,
  programFiltersIds,
  SearchFilterConfigurationStatus,
} from 'constants/programs';
import searchItems from 'utils/searchItems';

import { BestPricingNoticeMessage, EmptyStateMessage } from './ProgramsAndPricing.constants';

export const getEmptyStateMessage = ({
  isOBEngineType,
  isProgramChangeAllowed,
  programChangeReasons,
  queryFilters,
}) => {
  if (!isProgramChangeAllowed) {
    return programChangeReasons.length
      ? `${EmptyStateMessage.PROGRAM_CHANGE_NOT_ALLOWED} ${programChangeReasons.join('. ')}`
      : EmptyStateMessage.PROGRAM_CHANGE_NOT_ALLOWED;
  }

  if (_.isNull(queryFilters) && isOBEngineType) {
    return EmptyStateMessage.CLICK_SEARCH;
  }

  return EmptyStateMessage.NO_RESULTS;
};

export const getBestPricingNoticeMessage = isFilterPanelHidden =>
  isFilterPanelHidden
    ? BestPricingNoticeMessage.WITHOUT_FILTER_PANEL
    : BestPricingNoticeMessage.WITH_FILTER_PANEL;

export const getIsBestPricingNoticeVisible = ({
  isBestPricingSearch,
  isLastSearchSucceeded,
  isLoading,
  loanProgramsOptionsGroupsWithDetails,
}) =>
  isBestPricingSearch &&
  isLastSearchSucceeded &&
  !isLoading &&
  !loanProgramsOptionsGroupsWithDetails.some(
    ({ loanProgramOptionsGroup, programDetails }) =>
      programDetails?.eligibility?.isAllowed &&
      loanProgramOptionsGroup.some(({ isStubOption }) => !isStubOption),
  );

const getServerPendingChange = pendingChange =>
  pendingChange?.actionType
    ? { ...pendingChange, newValue: pendingChange.serverNewValue ?? pendingChange.newValue }
    : null;

export const formatPendingChangesForHighlighting = pendingChanges => {
  const serverProductId = getServerPendingChange(pendingChanges.productId);
  const serverProgramId = getServerPendingChange(pendingChanges.programId);
  const serverLockTerm = getServerPendingChange(pendingChanges.lockTerm);
  const serverFinalBuyPrice = getServerPendingChange(pendingChanges.finalBuyPrice);
  const serverRate = getServerPendingChange(pendingChanges.interestRate);

  return {
    ...pendingChanges,
    programId: serverProgramId || serverProductId,
    productId: serverProductId,
    lockTerm: serverLockTerm,
    finalBuyPrice: serverFinalBuyPrice,
    rate: serverRate,
  };
};

export const getSelectedFilterValues = filterOptions =>
  _.reduce(
    filterOptions,
    (acc, options, filterId) => {
      const selectedValues = _.filter(options, 'isSelected').map(({ value }) => value);

      return selectedValues.length ? { ...acc, [filterId]: selectedValues } : acc;
    },
    {},
  );

export const getIsSearchFilterPreloaded = ({ filterOptions, searchFilter }) => {
  if (_.isNil(filterOptions) || _.isEmpty(searchFilter)) {
    return false;
  }

  const filterIdsWithSelectedOptions = _.keys(getSelectedFilterValues(filterOptions));

  if (_.isEmpty(filterIdsWithSelectedOptions)) {
    return false;
  }

  return _.every(filterIdsWithSelectedOptions, filterId => !_.isEmpty(searchFilter[filterId]));
};

export const getShouldPrefillSearchFilters = ({
  isOBEngineType,
  isLocked,
  isProgramReselection,
  searchFilterConfigurationStatus,
}) =>
  isOBEngineType &&
  searchFilterConfigurationStatus === SearchFilterConfigurationStatus.LOADED &&
  (isLocked || isProgramReselection);

export const getShouldAutoSearchPrograms = ({
  isOBEngineType,
  isProgramReselection,
  isSearchFilterPreloaded,
}) => isOBEngineType && isProgramReselection && isSearchFilterPreloaded;

// A hidden panel with nothing to prefill would leave the user on "Click Search" with no Search button.
export const getIsFilterPanelHidden = ({
  isFromPropertyAndLoan,
  isOBEngineType,
  isLocked,
  searchFilterConfigurationStatus,
  filterOptions,
}) => {
  if (isFromPropertyAndLoan) {
    return false;
  }

  if (!isOBEngineType) {
    return true;
  }

  if (!isLocked) {
    return false;
  }

  switch (searchFilterConfigurationStatus) {
    case SearchFilterConfigurationStatus.LOADED:
      return !_.isEmpty(getSelectedFilterValues(filterOptions));

    case SearchFilterConfigurationStatus.FAILED:
      return false;

    default:
      return true;
  }
};

const getIsLoanProgramOptionsGroupExpired = loanProgramOptionsGroup =>
  loanProgramOptionsGroup.some(({ priceStatus }) => priceStatus === LoanOptionPriceStatus.EXPIRED);

export const getFilteredLoanProgramOptionGroups = ({
  searchText,
  loanProgramsOptionsGroupsWithDetails,
  isOBEngineType,
  searchFilter,
  isHideNonEligible,
  isShowExpiredPricing,
  isExpiredPricingSaveEnabled,
}) => {
  return searchItems(i => [i.loanProgramOptionsGroup[0].internalName])(
    searchText,
    loanProgramsOptionsGroupsWithDetails,
  )
    .map(loanProgramOptionGroup => {
      const { loanProgramOptionsGroup, programDetails, ...rest } = loanProgramOptionGroup;
      const filterValuesArr = Object.entries(searchFilter);

      if (isOBEngineType) {
        return loanProgramOptionGroup;
      }

      return {
        loanProgramOptionsGroup: loanProgramOptionsGroup.filter(programOption =>
          filterValuesArr.every(([key, values]) => {
            if (!values?.length || !LoanProgramsFilterIds[key]) {
              return true;
            }

            return values.includes(
              _.get(
                programFiltersIds.includes(key) ? programDetails : programOption,
                LoanProgramsFilterIds[key],
              ),
            );
          }),
        ),
        programDetails,
        ...rest,
      };
    })
    .filter(i => i.loanProgramOptionsGroup.length)
    .filter(i => (isHideNonEligible ? i.programDetails.eligibility.isAllowed : true))
    .filter(
      i =>
        !isExpiredPricingSaveEnabled ||
        isShowExpiredPricing ||
        !getIsLoanProgramOptionsGroupExpired(i.loanProgramOptionsGroup),
    );
};
